import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { TablerIconsModule } from 'angular-tabler-icons';
import { Subject, Observable, takeUntil, debounceTime, distinctUntilChanged, finalize, switchMap, take } from 'rxjs';
import { MaterialModule } from 'src/app/material.module';
import { PaginationResult } from 'src/app/models/common.response';
import { AppointmentTypeService } from './appointment-type.service';
import { MatDialog } from '@angular/material/dialog';
import { AppointmentDetailComponent } from './appointment-detail/appointment-detail.component';
import { DeleteDialog } from 'src/app/pages/dialogs/delete.component';
import { ToastrService } from 'ngx-toastr';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LookupService } from 'src/app/services/lookup.service';
import { CommonDropDown } from 'src/app/common/types/dropdown.type';
import { AppointmentType, AppointmentTypeLabels } from 'src/app/common/enums/appointment-type';
import { IAppointmentType } from './iappointment-type';
import { TranslationUtils } from 'src/app/common/utils/translation.utils';
import { PAGE_SIZE_OPTIONS } from 'src/app/common/constant/constants.type';


@Component({
  selector: 'app-appointment-type',
  standalone: true,
  imports: [CommonModule, TablerIconsModule, MaterialModule, ReactiveFormsModule, TranslateModule],
  templateUrl: './appointment-type.component.html',
  styleUrl: './appointment-type.component.scss'
})
export class AppointmentTypeComponent implements OnInit, AfterViewInit, OnDestroy {

  displayedColumns: string[] = [
    // '#',
    'AppointmentType',
    'Charge',
    'action',
  ];
  dataSource = new MatTableDataSource<IAppointmentType>();
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator =
    Object.create(null);

  private _unsubscribeAll: Subject<any> = new Subject<any>();
  pagination: PaginationResult;
  appointmenttypecount: number = 0;
  appointmenttypes$: Observable<IAppointmentType[]>;
  appointmenttypedata: IAppointmentType[];
  isLoading: boolean = false;
  pageSizeOptions = PAGE_SIZE_OPTIONS;

  isLoadData: boolean;
  searchappointmenttype: FormControl = new FormControl();
  searchQuery: string;
  listofBillCharge: CommonDropDown[];
  public disabledTypes = Object.values(AppointmentType).filter(value => typeof value === 'number');
  private destroy$ = new Subject<void>();

  constructor(
    private _appointmentTypeService: AppointmentTypeService,
    private dialog: MatDialog,
    private toastr: ToastrService,
    private translate: TranslateService,
    private _lookupService: LookupService,
    private translationUtils: TranslationUtils
  ) { }

  ngOnInit(): void {
    this.isLoadData = true;
    this._appointmentTypeService.pagination$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((pagination: PaginationResult | null) => {
        if (pagination) {
          this.pagination = pagination;
          this.appointmenttypecount = this.pagination.rowCount;
        }
      });

    this._lookupService.billcharges$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((billcharge: CommonDropDown[]) => {
        this.listofBillCharge = billcharge;
      });

    this._appointmentTypeService.appointmenttypes$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((data) => {
        this.appointmenttypedata = data;
        this.dataSource = new MatTableDataSource(this.appointmenttypedata); // Correct initialization
        this.dataSource.paginator = this.paginator;
      });
    this.isLoadData = false;
    this.searchappointmenttype.valueChanges.pipe(
      takeUntil(this._unsubscribeAll),
      debounceTime(500),
      distinctUntilChanged(),
      switchMap((query) => {
        this.isLoading = true;
        return this._appointmentTypeService.getAppointmentType({
          pageNumber: 0,
          pageSize: this.pagination.pageSize,
          isDescending: false,
          searchValue: query || ''
        }).pipe(
          finalize(() => (this.isLoading = false))
        );
      })
    ).subscribe((response) => {
      if (response && response.result) {
        this.dataSource.data = response.result.appointments;
        this.appointmenttypecount = response.result.pagination.rowCount;
      }
    });
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  onAppointmentType() {
    this.dialog.open(AppointmentDetailComponent, {
      width: '600px',
      height: 'auto',
      disableClose: true,
    }).afterClosed().subscribe((result) => {
      if (result) {
        this.billChargeLookup();
        this.pagination.currentPage = 0;
        this.refreshAppointmentType();
      }
    });
  }

  onUpdateAppointmentType(appointment: IAppointmentType) {
    this.dialog.open(AppointmentDetailComponent, {
      width: '600px',
      height: 'auto',
      disableClose: true,
      data:
      {
        appointmenttype: appointment,
        isEdit: true
      }
    }).afterClosed().subscribe((result) => {
      if (result) {
        this.billChargeLookup();
        this.refreshAppointmentType();
      }
    });
  }

  onDeleteAppointmentType(id: number) {
    const confirmation = this.dialog.open(DeleteDialog, {
      width: 'auto',
      enterAnimationDuration: '300ms',
      exitAnimationDuration: '300ms',
      disableClose: true,
      data: { message: this.translate.instant('DELETE_DIALOG') + ' ' + this.translate.instant('ENTITY.APPOINTMENT_TYPE') + ' ?' }
    })
    confirmation.afterClosed().subscribe((result) => {
      if (result === "confirm") {
        this._appointmentTypeService.deleteAppointmenttype(id).subscribe((response) => {
          if (response.isSuccess) {
            this.refreshAppointmentType();
            this.toastr.success(this.translationUtils.getTranslatedMessage('COMMON.DELETE', this.translate.instant('ENTITY.APPOINTMENT_TYPE')));
          }
          else {
            const allErrors = Object.values(response.errors).flat();
            this.toastr.error(allErrors.join("\n"));

          }
        })
      }
    });
  }

  refreshAppointmentType() {
    const globalSearchValue = this.searchappointmenttype?.value?.trim();
    const requestPayload: any = {
      pageNumber: 0,
      pageSize: this.pagination?.pageSize || 100,
      isDescending: false
    };

    if (globalSearchValue) {
      requestPayload.SearchValue = globalSearchValue;
    } else {
      requestPayload.pageNumber = this.pagination?.currentPage || 0;
    }

    this._appointmentTypeService.getAppointmentType(requestPayload)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe((response) => {
        if (response && response.result) {
          const AppointmentType = response.result.appointments;
          this.appointmenttypedata = AppointmentType;
          if (!this.dataSource) {
            this.dataSource = new MatTableDataSource(AppointmentType);
          } else {
            this.dataSource.data = AppointmentType;
          }
          this.dataSource.paginator = this.paginator;
          this.appointmenttypecount = response.result.pagination.rowCount;
          this.pagination = response.result.pagination;
        }
      });

  }

  getBillCharge(billchargeId: number): string {
    if (!this.listofBillCharge) return 'Unknown'; // Ensure the list exists
    const billcharge = this.listofBillCharge.find(cat => cat.id === billchargeId);
    return billcharge ? `${billcharge.name}  (₹ ${billcharge.childValue})` : 'Unknown';
  }

  billChargeLookup() {
    this._lookupService.billcharges$
      .pipe(take(1))
      .subscribe((data) => {
        this.listofBillCharge = data;
      })
  }

  isActionDisabled(appointmentType: string): boolean {
    const enumValue = Object.entries(AppointmentTypeLabels)
      .find(([_, label]) => label === appointmentType)?.[0];
    return enumValue ? this.disabledTypes.includes(+enumValue) : false;
  }

}